import React, { useEffect, useRef, useState } from 'react'

import * as S from './styles'
import { CountUpCard } from '@/components'
import { ReviewCard } from '@/service/api/home'

export type ReviewContainerProps = {
  /**
   * Numerical card data
   * @example
   * ```tsx
   * <ReviewContainer cards={cards} />
   * ```
   * */
  cards: ReviewCard[]

  /**
   * If it should start counting immediately after rendering
   * @example
   * ```tsx
   * <ReviewContainer cards={cards} />
   * ```
   * */
  countOnRender?: boolean
}

export const ReviewContainer: React.FC<ReviewContainerProps> = ({
  cards,
  countOnRender = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [startCount, setStartCount] = useState(countOnRender)
  const [isIntersecting, setIsIntersecting] = useState(false)

  useEffect(() => {
    if (!containerRef.current) return

    const observer = new IntersectionObserver(([entry]) => {
      setIsIntersecting(entry.isIntersecting)
    })

    observer.observe(containerRef.current)
    // eslint-disable-next-line consistent-return
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (isIntersecting) {
      setStartCount(true)
    }
  }, [isIntersecting])

  if (!cards.length) return null

  return (
    <S.FlexContainerReviews padding="0">
      <S.ReviewContainer ref={containerRef}>
        {cards?.map((card) => (
          <CountUpCard
            key={card.description}
            value={card.value}
            description={card.description}
            prefix={card.prefix}
            suffix={card.suffix}
            startCount={startCount}
          />
        ))}
      </S.ReviewContainer>
    </S.FlexContainerReviews>
  )
}
